<template>
  <div :class="$style.pinCode">
    <input
      v-for="(item, i) in numInputs"
      :key="i"
      ref="inputs"
      :class="[$style.singleInput, $style.input]"
      inputmode="numeric"
      max="9"
      maxlength="1"
      min="0"
      pattern="[0-9]*"
      :shouldAutoFocus="shouldAutoFocus && i === 0"
      type="text"
      :value="pin[i]"
      @focus="() => focusInput(i)"
      @input="($e) => handleOnChange(i, $e)"
      @keyup.delete="handleDelete"
      @keyup.left="focusPrevInput"
      @keyup.right="focusNextInput"
      @paste="handleOnPaste"
    >
  </div>
</template>

<script>
export default {
  name: 'PinCode',
  props: {
    /**
     * Pin Length
     */
    numInputs: {
      default: 6,
      type: Number,
    },

    /**
     * Auto focuses input on inital page load.
     */
    shouldAutoFocus: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeInput: 0,
      pin: [],
    };
  },

  /**
   * When mounted reset the active pin and pin value, and focus on the first input when the
   * shouldAutoFocus property is set to true.
   */
  mounted() {
    this.activeInput = 0;
    this.pin = new Array(this.numInputs);

    if (this.$refs.inputs && this.shouldAutoFocus) {
      this.focusInput(this.activeInput);
    }
  },

  methods: {
    blurInput(index) {
      this.$refs.inputs[index].blur();
      this.activeInput = -1;
    },

    // Helper to return Pin from input
    checkFilledAllInputs() {
      if (this.pin.join('').length === this.numInputs) {
        this.$emit('complete', this.pin.join(''));
      } else {
        this.$emit('incomplete');
      }
    },

    // Focus on input by index
    focusInput(index) {
      if (index !== this.activeInput) {
        this.activeInput = index;
      }

      this.$refs.inputs[index].focus();
    },

    focusNextInput() {
      if (this.activeInput + 1 < this.numInputs) {
        this.activeInput += 1;
        this.focusInput(this.activeInput);
      } else {
        this.blurInput(this.activeInput);
      }
    },

    focusPrevInput() {
      if (this.activeInput - 1 >= 0) {
        this.activeInput -= 1;
        this.focusInput(this.activeInput);
      } else {
        this.blurInput(this.activeInput);
      }
    },

    handleDelete(event) {
      const { value } = event.target;

      if (!value) {
        this.focusPrevInput();
      }

      this.checkFilledAllInputs();
    },

    handleOnChange(index, event) {
      const { value } = event.target;

      // Update the pin that the user has entered and if the pin is complete then inform the parent
      if (this.isCodeNumeric(value)) {
        this.pin[index] = value;
      } else {
        this.$refs.inputs[this.activeInput].value = '';
        this.pin[index] = '';
        return;
      }

      // If no value has been entered, then do nothing. Allow the user to _clear_ the value they
      // entered _without_ navigating elsewhere. Navigating to the next input when the user simply
      // wants to erase the value they entered is a bad UX.
      if (!value) {
        return;
      }

      // When a value has been entered, navigate to the next input if possible, otherwise blur the
      // last input if that's what was focused.
      if (index + 1 < this.numInputs) {
        this.activeInput += 1;
        this.focusInput(this.activeInput);
      } else {
        this.blurInput(this.activeInput);
      }

      this.checkFilledAllInputs();
    },

    handleOnPaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData
        .getData('text/plain')
        .slice(0, this.numInputs - this.activeInput)
        .split('');

      if (!pastedData.join('').match(/^\d+$/)) {
        return 'Invalid pasted data';
      }

      // Paste data from focused input onwards
      const currentCharsInPin = this.pin.slice(0, this.activeInput);
      const combinedWithPastedData = currentCharsInPin.concat(pastedData);
      this.$set(this, 'pin', combinedWithPastedData.slice(0, this.numInputs));
      this.focusInput(combinedWithPastedData.slice(0, this.numInputs).length - 1);

      return this.checkFilledAllInputs();
    },

    isCodeNumeric(charCode) {
      return ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(charCode);
    },
  },
};
</script>

<style module>
@import '../css/FormValidationFields.css';

.pinCode {
  display: flex;
  justify-content: center;
}

.pinCode .singleInput {
  height: 45px;
  margin: 0 3px;
  text-align: center;
  width: 35px;
}
</style>
